import { PRIORITIES } from './priority.js';
import { isOfficialHoliday } from './holidays.js';
export const DEFAULT_CALENDAR = { timezone: 'America/Mexico_City', startHour: 9, endHour: 18, country: 'MX' };
const h = (value) => ({ value, unit: 'h' });
const d = (value) => ({ value, unit: 'd' });
/**
 * Estándar SAP de soporte (SAP Enterprise Support / nota 67739):
 *  - Very High (P1): reacción inicial 1 h y acción correctiva 4 h, 24×7.
 *  - High (P2): reacción inicial 4 h en horario de oficina.
 *  - Medium y Low no tienen meta contractual en SAP: se usa la convención de AMS SAP
 *    (1 y 2 días hábiles de respuesta) y resolución de 1/3/5 días hábiles.
 */
export const DEFAULT_SLA_POLICY = {
    response: { P1: h(1), P2: h(4), P3: d(1), P4: d(2) },
    resolution: { P1: h(4), P2: d(1), P3: d(3), P4: d(5) },
    estimate: d(2),
    p1RoundTheClock: true
};
const validDuration = (v) => {
    const x = v;
    return Boolean(x) && (x.unit === 'h' || x.unit === 'd') && Number.isFinite(x.value) && x.value > 0 && x.value <= 1000;
};
/** Completa una política guardada (parcial o de versiones anteriores) con el estándar. */
export function normalizeSlaPolicy(raw) {
    const pick = (m, base) => Object.fromEntries(PRIORITIES.map(p => [p, validDuration(m?.[p]) ? { ...m[p] } : { ...base[p] }]));
    return {
        response: pick(raw?.response, DEFAULT_SLA_POLICY.response),
        resolution: pick(raw?.resolution, DEFAULT_SLA_POLICY.resolution),
        estimate: validDuration(raw?.estimate) ? { ...raw.estimate } : { ...DEFAULT_SLA_POLICY.estimate },
        p1RoundTheClock: raw?.p1RoundTheClock ?? DEFAULT_SLA_POLICY.p1RoundTheClock
    };
}
const HOUR = 3_600_000;
export const calendarFor = (clock, priority, policy = DEFAULT_SLA_POLICY) => clock !== 'estimate' && priority === 'P1' && policy.p1RoundTheClock ? '24x7' : 'business';
export function durationMs(t, kind, cal = DEFAULT_CALENDAR) {
    if (t.unit === 'h')
        return t.value * HOUR;
    return t.value * (kind === '24x7' ? 24 : cal.endHour - cal.startHour) * HOUR;
}
/** Meta en ms del calendario que le toca (hábil o 24×7). */
export function slaTargetMs(clock, priority, cal = DEFAULT_CALENDAR, policy = DEFAULT_SLA_POLICY) {
    const t = clock === 'estimate' ? policy.estimate : policy[clock][priority];
    return durationMs(t, calendarFor(clock, priority, policy), cal);
}
// ---------- tiempo hábil ----------
function tzOffsetMs(date, timezone) {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone, hourCycle: 'h23',
        year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit'
    }).formatToParts(date);
    const get = (t) => Number(parts.find(p => p.type === t)?.value);
    const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
    return asUtc - Math.floor(date.getTime() / 1000) * 1000;
}
/** Instante UTC de una hora local de un día local (y, m, d) en la zona dada. */
function localToUtc(y, m, d, hour, timezone) {
    const guess = Date.UTC(y, m, d, hour);
    return guess - tzOffsetMs(new Date(guess), timezone);
}
function isHoliday(cal, y, m, d) {
    if (!cal.country && !cal.extraHolidays?.length)
        return false;
    const date = `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    return Boolean((cal.country && isOfficialHoliday(cal.country, date)) || cal.extraHolidays?.includes(date));
}
/** Milisegundos hábiles entre dos instantes según el calendario (sin fines de semana ni festivos). */
export function businessMsBetween(from, to, kind, cal = DEFAULT_CALENDAR) {
    const start = from.getTime();
    const end = to.getTime();
    if (end <= start)
        return 0;
    if (kind === '24x7')
        return end - start;
    let total = 0;
    const local = new Date(start + tzOffsetMs(from, cal.timezone));
    let y = local.getUTCFullYear(), m = local.getUTCMonth(), d = local.getUTCDate();
    for (let i = 0; i < 800; i++) {
        const dayStartUtc = localToUtc(y, m, d, 0, cal.timezone);
        if (dayStartUtc > end)
            break;
        const dow = new Date(Date.UTC(y, m, d)).getUTCDay();
        if (dow !== 0 && dow !== 6 && !isHoliday(cal, y, m, d)) {
            const open = localToUtc(y, m, d, cal.startHour, cal.timezone);
            const close = localToUtc(y, m, d, cal.endHour, cal.timezone);
            total += Math.max(0, Math.min(close, end) - Math.max(open, start));
        }
        const next = new Date(Date.UTC(y, m, d + 1));
        y = next.getUTCFullYear();
        m = next.getUTCMonth();
        d = next.getUTCDate();
    }
    return total;
}
// ---------- ciclo del reloj ----------
/** ¿El tipo de ticket tiene SLA? Solo incidentes y solicitudes de servicio. */
export const hasSla = (type) => type === 'INC' || type === 'SOL';
export function startSla(type, priority, now) {
    if (!hasSla(type))
        return null;
    const clock = 'resolution';
    return {
        clock, calendar: calendarFor(clock, priority), startedAt: now.toISOString(), accumulatedMs: 0,
        runningSince: now.toISOString(), stoppedAt: null, trackResponse: true
    };
}
function consumed(sla, now, cal) {
    return sla.accumulatedMs + (sla.runningSince ? businessMsBetween(new Date(sla.runningSince), now, sla.calendar, cal) : 0);
}
// Decisión 26-oct-2026: esperar a un tercero (SAP u otro proveedor) SIEMPRE pausa el SLA.
const PAUSING_HOLDS = ['customer', 'third_party', 'scheduled_change'];
/** ¿El reloj debe correr en este estado? */
function shouldRun(sla, status, holdReason) {
    if (['closed', 'cancelled', 'rejected', 'converted'].includes(status))
        return 'stop';
    if (sla.clock === 'resolution' && status === 'resolved')
        return 'stop';
    if (sla.clock === 'estimate' && ['pending_approval', 'approved', 'in_progress', 'resolved'].includes(status))
        return 'stop';
    if (status === 'on_hold' && holdReason && PAUSING_HOLDS.includes(holdReason))
        return 'pause';
    return 'run';
}
/** Ajusta el reloj al nuevo estado del ticket. Devuelve el mismo objeto si no cambia. */
export function updateSla(sla, status, holdReason, priority, now, cal = DEFAULT_CALENDAR, policy = DEFAULT_SLA_POLICY) {
    if (!sla)
        return sla;
    sla = { ...sla, calendar: calendarFor(sla.clock, priority, policy) };
    const mode = shouldRun(sla, status, holdReason);
    // Un ticket reabierto o un estimado que el cliente pidió ajustar vuelven a correr.
    if (mode === 'run') {
        if (sla.runningSince)
            return sla;
        return { ...sla, runningSince: now.toISOString(), stoppedAt: null };
    }
    const accumulatedMs = consumed(sla, now, cal);
    if (mode === 'pause')
        return { ...sla, accumulatedMs, runningSince: null };
    if (sla.stoppedAt && !sla.runningSince)
        return sla;
    return { ...sla, accumulatedMs, runningSince: null, stoppedAt: now.toISOString() };
}
/** Reclasificar arranca el reloj que corresponde al nuevo tipo. */
export function restartSla(type, priority, status, holdReason, now, cal = DEFAULT_CALENDAR, policy = DEFAULT_SLA_POLICY, keepResponse = true) {
    const fresh = startSla(type, priority, now);
    if (fresh && !keepResponse)
        delete fresh.trackResponse;
    return updateSla(fresh, status, holdReason, priority, now, cal, policy);
}
export const SLA_WARNING_PCT = 0.75;
const NO_SLA = { clock: null, health: 'none', pct: 0, elapsedMs: 0, targetMs: 0, remainingMs: 0, calendar: null, running: false };
export function slaView(sla, type, priority, now, cal = DEFAULT_CALENDAR, policy = DEFAULT_SLA_POLICY) {
    if (!sla || sla.clock === 'estimate')
        return NO_SLA;
    // El calendario sigue a la prioridad actual (un P1 corre 24×7).
    const calendar = calendarFor(sla.clock, priority, policy);
    const effective = { ...sla, calendar };
    const elapsedMs = consumed(effective, now, cal);
    const targetMs = slaTargetMs(sla.clock, priority, cal, policy);
    const pct = targetMs ? elapsedMs / targetMs : 0;
    let health;
    if (pct >= 1)
        health = 'breached';
    else if (sla.stoppedAt && !sla.runningSince)
        health = 'met';
    else if (!sla.runningSince)
        health = 'paused';
    else
        health = pct >= SLA_WARNING_PCT ? 'warning' : 'ok';
    return { clock: sla.clock, health, pct, elapsedMs, targetMs, remainingMs: targetMs - elapsedMs, calendar, running: Boolean(sla.runningSince) };
}
/**
 * Reloj de primera respuesta: de la creación a la primera respuesta pública del agente.
 * No se pausa; si el ticket se cierra sin respuesta (cancelado, duplicado) deja de contar.
 */
export function responseView(t, now, cal = DEFAULT_CALENDAR, policy = DEFAULT_SLA_POLICY) {
    if (!t.sla?.trackResponse || t.sla.clock === 'estimate')
        return NO_SLA;
    if (!t.firstResponseAt && !t.open)
        return NO_SLA;
    const calendar = calendarFor('response', t.priority, policy);
    const end = t.firstResponseAt ?? now;
    const elapsedMs = businessMsBetween(t.createdAt, end, calendar, cal);
    const targetMs = slaTargetMs('response', t.priority, cal, policy);
    const pct = targetMs ? elapsedMs / targetMs : 0;
    const health = t.firstResponseAt
        ? (pct > 1 ? 'breached' : 'met')
        : pct >= 1 ? 'breached' : pct >= SLA_WARNING_PCT ? 'warning' : 'ok';
    return { clock: 'response', health, pct, elapsedMs, targetMs, remainingMs: targetMs - elapsedMs, calendar, running: !t.firstResponseAt };
}
/** Lo más urgente entre los relojes que siguen corriendo: vencido > por vencer > en tiempo. */
export function slaRisk(...views) {
    const live = views.filter(v => v.running);
    if (live.some(v => v.health === 'breached'))
        return 'breached';
    if (live.some(v => v.health === 'warning'))
        return 'warning';
    return 'ok';
}
/** Ms al próximo vencimiento entre los relojes que corren (negativo si ya venció; Infinity si ninguno corre). */
export function nextDueMs(...views) {
    const live = views.filter(v => v.running);
    return live.length ? Math.min(...live.map(v => v.remainingMs)) : Infinity;
}
export const SUPPORT_MODEL_LABEL = {
    bag: 'Bolsa de horas', baseline: 'Baseline', on_demand: 'On Demand', none: 'Sin proyecto de soporte'
};
export const DEFAULT_COMMITMENT = { bag: 'reference', baseline: 'committed', on_demand: 'none', none: 'reference' };
export function normalizeCommitment(raw) {
    const ok = (v) => v === 'committed' || v === 'reference' || v === 'none';
    return Object.fromEntries(Object.keys(DEFAULT_COMMITMENT)
        .map(k => [k, ok(raw?.[k]) ? raw[k] : DEFAULT_COMMITMENT[k]]));
}
/** Compromiso del SLA según el modelo de soporte del proyecto del ticket (Veta). */
export function commitmentFor(supportModel, rules = DEFAULT_COMMITMENT) {
    const key = ['bag', 'baseline', 'on_demand'].includes(supportModel ?? '') ? supportModel : 'none';
    return rules[key];
}
//# sourceMappingURL=sla.js.map